/*
  One Tooth World — <otw-announcement> (PRD §6.1).
  Rotates messages with a vertical slide, pauses on hover / focus / hidden tab,
  and remembers a dismissal in sessionStorage. Talks to other modules via window.OTW.
*/
const OTW = (window.OTW = window.OTW || {});
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class OtwAnnouncement extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('.otw-announcement__item'));
    this.index = 0;
    this.interval = Number(this.dataset.interval) || 4000;
    this.paused = false;

    this.closeButton = this.querySelector('.otw-announcement__close');
    this.closeButton?.addEventListener('click', () => this.dismiss());

    if (this.items.length > 1) {
      const pause = () => this.pause();
      const resume = () => this.resume();
      this.addEventListener('mouseenter', pause);
      this.addEventListener('mouseleave', resume);
      this.addEventListener('focusin', pause);
      this.addEventListener('focusout', resume);
      this.onVisibility = () => (document.hidden ? this.stop() : this.start());
      document.addEventListener('visibilitychange', this.onVisibility);
      this.start();
    }

    // Theme editor: show the selected block and hold it
    this.onBlockSelect = (event) => {
      const item = this.items.find((el) => el.contains(event.target) || el === event.target);
      if (!item) return;
      this.pause();
      this.show(this.items.indexOf(item));
    };
    this.onBlockDeselect = () => this.resume();
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.onVisibility);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  start() {
    this.stop();
    if (this.paused || this.items.length < 2) return;
    this.timer = setInterval(() => this.show(this.index + 1), this.interval);
  }

  stop() {
    clearInterval(this.timer);
    this.timer = null;
  }

  pause() {
    this.paused = true;
    this.stop();
  }

  resume() {
    if (this.contains(document.activeElement) || this.matches(':hover')) return;
    this.paused = false;
    this.start();
  }

  show(next) {
    const target = (next + this.items.length) % this.items.length;
    if (target === this.index) return;
    const current = this.items[this.index];
    const incoming = this.items[target];

    current.classList.remove('is-active');
    current.setAttribute('aria-hidden', 'true');
    current.inert = true;
    if (!reducedMotion()) {
      current.classList.add('is-leaving');
      setTimeout(() => current.classList.remove('is-leaving'), 450);
    }

    incoming.classList.remove('is-leaving');
    incoming.classList.add('is-active');
    incoming.removeAttribute('aria-hidden');
    incoming.inert = false;
    this.index = target;
  }

  dismiss() {
    try {
      sessionStorage.setItem(this.dataset.dismissKey, '1');
    } catch (e) {
      /* storage blocked: hide for this page view only */
    }
    this.stop();
    const finish = () => {
      this.hidden = true;
      OTW.emit?.('announcement:dismissed', { id: this.dataset.dismissKey });
    };
    if (reducedMotion()) return finish();
    this.style.height = `${this.offsetHeight}px`;
    requestAnimationFrame(() => {
      this.style.height = '0px';
      this.addEventListener('transitionend', finish, { once: true });
      setTimeout(finish, 400);
    });
  }
}

if (!customElements.get('otw-announcement')) customElements.define('otw-announcement', OtwAnnouncement);
